import { SolutionItemData, buildEquipmentItem, getDescriptionTypographyStyles } from './utils';
import { Box, Button, Card, CircularProgress, Tooltip, Typography } from '@mui/material';
import { getBlueprintCalculations } from 'features/blueprints/utils';
import { Icons } from 'greener-ui-library';
import { useGetEquipmentDetails, useGetRentalBatteryDetails } from 'hooks/useEquipment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Blueprint } from 'types';

interface ProposedSolutionBoxProps {
  blueprintDetails?: Blueprint;
  readOnly?: boolean;
}

export const ProposedSolutionBox = ({ blueprintDetails, readOnly = false }: ProposedSolutionBoxProps) => {
  const { t } = useTranslation();

  // Get hybrid calculation from blueprint
  const { hybrid } = getBlueprintCalculations(blueprintDetails);
  const powerSetup = hybrid?.powerSetup;

  // Fetch equipment if selected from catalog
  const generatorTypeId = powerSetup?.generatorTypeId;
  const {
    equipment: generatorEquipment,
    isLoading: isGeneratorLoading,
    error: generatorError,
  } = useGetEquipmentDetails(generatorTypeId?.toString() ?? '');

  const batteryTypeId = powerSetup?.batteryTypeId;
  // Batteries are always from Greener catalog - use rental-batteries endpoint
  const {
    equipment: batteryEquipment,
    isLoading: isBatteryLoading,
    error: batteryError,
  } = useGetRentalBatteryDetails(batteryTypeId?.toString() ?? '');

  // Build solution items
  const items: SolutionItemData[] = useMemo(() => {
    // Battery (left)
    const batteryManualDescriptions: string[] = [];
    if (powerSetup?.batterySizeKwh) {
      batteryManualDescriptions.push(`${powerSetup.batterySizeKwh} kWh`);
    }
    if (powerSetup?.batteryConverterPowerKw) {
      batteryManualDescriptions.push(`${powerSetup.batteryConverterPowerKw} kW`);
    }

    const batteryItem = buildEquipmentItem({
      equipmentTypeId: batteryTypeId,
      equipment: batteryEquipment,
      placeholderImage: '/static/images/greener-box-forest.svg',
      title: t('blueprints:details.proposedSolution.battery.title'),
      manualDescriptions: batteryManualDescriptions,
      count: powerSetup?.batteryCount,
      isError: !!batteryError && !!batteryTypeId,
      errorMessage: batteryError
        ? t('blueprints:wizard.fields.suggestedRentals.equipment.noEquipmentFound')
        : undefined,
    });

    // Generator (center)
    const generatorManualDescriptions = powerSetup?.generatorCapacityKva
      ? [`${powerSetup.generatorCapacityKva} kVA`]
      : [];

    const generatorItem = buildEquipmentItem({
      equipmentTypeId: generatorTypeId,
      equipment: generatorEquipment,
      placeholderImage: '/static/images/generator-forest.svg',
      title: t('blueprints:details.proposedSolution.generator.title'),
      manualDescriptions: generatorManualDescriptions,
      count: powerSetup?.generatorCount,
      isError: !!generatorError && !!generatorTypeId,
      errorMessage: generatorError
        ? t('blueprints:wizard.fields.suggestedRentals.equipment.noEquipmentFound')
        : undefined,
    });

    // Monitoring & Control (right) - always placeholder
    const monitoringItem: SolutionItemData = {
      imagePath: '/static/images/data-forest.svg',
      title: t('blueprints:details.proposedSolution.monitoring.title'),
      descriptions: t('blueprints:details.proposedSolution.monitoring.description').split('\n'),
    };

    return [
      batteryItem, 
      ...(powerSetup?.generatorCount ? [generatorItem] : []), 
      monitoringItem
    ];
  }, [generatorTypeId, generatorEquipment, batteryTypeId, batteryEquipment, powerSetup, t]);

  const isLoading = isGeneratorLoading || isBatteryLoading;
  const alignments = ['flex-start', 'center', 'flex-end'];

  return (
    <Card
      data-testid="proposed-solution"
      sx={{ padding: 4, borderRadius: '8px' }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">{t('blueprints:details.proposedSolution.title')}</Typography>

        {!readOnly && (
          <Button
            data-testid="edit-proposed-solution-button"
            component={Link}
            to={`/solutions/${blueprintDetails?.blueprintId}/edit?step=3`}
          >
            {t('blueprints:details.proposedSolution.editButton')}
          </Button>
        )}
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-evenly"
          alignItems={{ xs: 'center', sm: 'flex-start' }}
          gap={3}
        >
          {items.map((item, index) => {
            // Show count badge and count in title only for battery/generator items with count > 1
            // Monitoring system (index 2) should never show count
            const isMonitoringItem = index === 2;
            const hasMultipleItems = item.count !== null && item.count !== undefined && item.count > 1;
            const shouldShowCount = hasMultipleItems && !isMonitoringItem;
            const imageSrc = item.imageUrl ?? item.imagePath;

            return (
              <Box
                key={index}
                display="flex"
                flexDirection="column"
                alignItems={{
                  xs: 'center',
                  sm: alignments[index],
                }}
              >
                <Box
                  sx={{
                    width: { xs: '172px', sm: '140px', md: '172px' },
                    height: { xs: '150px', sm: '120px', md: '150px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    overflow: 'visible',
                    position: 'relative',
                  }}
                >

                  <img
                    data-testid={`equipment-image-${index}`}
                    src={imageSrc}
                    alt={item.title || 'Equipment'}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }} />

                  {item.isError && (
                    <Tooltip title={item.errorMessage || ''} arrow>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '6px',
                          left: '6px',
                          width: '24px',
                          height: '24px',
                          backgroundColor: (theme) => theme.palette.warning.main,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 10,
                        }}
                      >
                        <Icons.Warning
                          sx={{
                            color: 'white',
                            fontSize: '16px',
                          }}
                        />
                      </Box>
                    </Tooltip>
                  )}
                  {shouldShowCount && (
                    <Box
                      data-testid={`equipment-count-badge-${index}`}
                      sx={{
                        position: 'absolute',
                        top: '6px',
                        right: '6px',
                        width: '30px',
                        height: '30px',
                        background: (theme) => theme.palette.primary.main,
                        border: '1px solid white',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'white',
                          fontWeight: 900,
                        }}
                      >
                        {item.count}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {item.title && (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1,
                      textAlign: 'center',
                      width: { xs: '172px', sm: '140px', md: '172px' },
                    }}
                  >
                    {shouldShowCount ? `${item.count} x ${item.title}` : item.title}
                  </Typography>
                )}

                {item.descriptions.map((description, descIndex) => (
                  <Typography
                    data-testid={`solution-item-description-${index}-${descIndex}`}
                    key={descIndex}
                    variant="body2"
                    color={
                      item.isError && descIndex === item.descriptions.length - 1 ? 'warning.main' : 'text.primary'
                    }
                    sx={{
                      ...getDescriptionTypographyStyles(),
                      ...(item.isError && descIndex === item.descriptions.length - 1
                        ? {
                            fontWeight: 500,
                          }
                        : {}),
                    }}
                  >
                    {description}
                  </Typography>
                ))}
              </Box>
            );
          })}
        </Box>
      )}
    </Card>
  );
};
